import { useEffect, useRef, useState } from 'react';
import ConnectionManageSurface from '@/components/connections/ConnectionManageSurface';
import { useLanguage } from '@/i18n/LanguageProvider';
import { useLibraryIdentity } from '@/hooks/useLibraryIdentity';
import { supabase } from '@/integrations/supabase/client';
import { MIRROR_FITNESS_CARDS_ENABLED } from '@/lib/featureFlags';
import { prepareFitnessDraftRestore, confirmFitnessDraftRestore, type FitnessDraftRestoreReceipt, type PreparedFitnessDraftRestore, type FitnessDraftRestoreBindings } from '@/lib/fitnessDraftRestore';
import type { FitnessDraftRestoreArgs } from '../../../supabase/functions/_shared/fitnessDraftRestoreDomain';
import type { FitnessLocalWorkoutEnvelope } from '../../../supabase/functions/_shared/fitnessLocalWorkoutDomain';

export interface FitnessDraftRestorePrefill {
  ownerId: string; sessionId: string; actionId: string; args: FitnessDraftRestoreArgs; request: FitnessLocalWorkoutEnvelope; isCurrent: () => boolean;
}
interface Props { prefill: FitnessDraftRestorePrefill | null; onClose: () => void; onLocalReceipt: (receipt: FitnessDraftRestoreReceipt, isCurrent: () => boolean) => Promise<boolean> }
const button = 'min-h-11 rounded-xl border border-border px-3 py-2 text-sm disabled:opacity-50';
const errorKey = (code: string) => 'mirror.fitnessDraftRestore.' + (
  code === 'invalid_session' ? 'authChanged' : code === 'review_expired' ? 'expired' : code === 'binding_changed' ? 'bindingChanged' :
  code === 'outcome_unknown' ? 'outcomeUnknown' : code === 'ack_pending' ? 'ackPending' : code === 'storage_unavailable' ? 'unavailable' :
  code === 'writer_active' || code === 'restore_busy' ? 'busy' : /runtime/.test(code) ? 'runtimeOccupied' : code === 'fitness_restore_disabled' ? 'unavailable' : 'changed');

export function FitnessDraftRestoreModal(props: Props) {
  const identity = useLibraryIdentity(), prefill = props.prefill;
  if (!prefill || !identity.ready || identity.userId !== prefill.ownerId || !prefill.isCurrent()) return null;
  return <RestoreReview key={[prefill.ownerId, identity.generation, prefill.sessionId, prefill.actionId].join(':')} {...props} prefill={prefill} />;
}
function RestoreReview({ prefill: incoming, onClose, onLocalReceipt }: Props & { prefill: FitnessDraftRestorePrefill }) {
  const { t, tf } = useLanguage(), epoch = useRef(0), locked = useRef(false);
  const [prefill] = useState(() => ({ ...incoming, args: structuredClone(incoming.args) }));
  const [origin] = useState(() => prefill.isCurrent), [prepared, setPrepared] = useState<PreparedFitnessDraftRestore | null>(null);
  const [saved, setSaved] = useState<FitnessDraftRestoreReceipt | null>(null), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const close = () => { epoch.current++; onClose(); };
  useEffect(() => {
    const run = epoch.current, current = () => run === epoch.current && origin();
    // Narrow the SDK to the read-only query surface; every returned row is
    // validated in the adapter before it can preserve a private binding.
    const bindings = supabase as unknown as FitnessDraftRestoreBindings;
    void prepareFitnessDraftRestore({ ...prefill, auth: supabase.auth, bindings, isCurrent: current }).then(review => {
      if (current()) setPrepared(review);
    }).catch(cause => { if (current()) setError(cause instanceof Error ? cause.message : 'outcome_unknown'); });
    const lifetime = epoch; return () => { lifetime.current++; };
  }, [origin, prefill]);
  const confirm = async () => {
    if (locked.current || !saved && (!prepared || !!error || !MIRROR_FITNESS_CARDS_ENABLED)) return;
    const run = epoch.current, current = () => run === epoch.current && origin();
    if (!current()) return;
    locked.current = true; setBusy(true); setError(null);
    try {
      const receipt = await confirmFitnessDraftRestore(prepared!);
      if (!current()) return;
      setSaved(receipt);
      if (!await onLocalReceipt(receipt, current) || !current()) throw new Error('ack_pending');
      close();
    } catch (cause) { if (current()) setError(cause instanceof Error ? cause.message : 'outcome_unknown'); }
    finally { locked.current = false; if (current()) setBusy(false); }
  };
  const summary = prepared?.summary, sets = summary?.exercises.flatMap(exercise => exercise.sets) ?? [];
  const key = 'mirror.fitnessDraftRestore.';
  return <ConnectionManageSurface open onOpenChange={open => { if (!open) close(); }} title={t(key + 'title')} description={t(key + 'description')} closeLabel={t('common.close')} brand={<span aria-hidden="true">🏋️</span>}>
    <div className="space-y-4 p-1">
      <p>{t(key + 'pausedMode')}</p>
      <p>{t(key + 'timingNotice')}</p>
      <p>{t(key + 'bindingsNotice')}</p>
      <p>{t(key + 'noHistoryWrite')}</p>
      {error && <p role="alert">{t(errorKey(error))}</p>}
      {!prepared && !error && <p role="status">{t(key + 'loading')}</p>}
      {summary && <dl className="space-y-3 text-sm">
        <div><dt className="text-muted-foreground">{t(key + 'workout')}</dt><dd className="whitespace-pre-wrap break-words">{summary.workoutName ?? t('fitnessAction.none')}</dd></div>
        <div><dt className="text-muted-foreground">{t(key + 'savedPhase')}</dt><dd>{t('mirror.fitness.localWorkout.' + summary.savedPhase)}</dd></div>
        <div><dt className="text-muted-foreground">{t(key + 'exercise')}</dt><dd className="whitespace-pre-wrap break-words">{summary.exercises[summary.currentExerciseIndex].name}</dd></div>
        <div><dt className="text-muted-foreground">{t(key + 'set')}</dt><dd>{summary.currentSet}</dd></div>
        <div><dt className="text-muted-foreground">{t(key + 'progress')}</dt><dd>{tf('fitnessDraftDiscard.counts', { done: sets.filter(set => set.done).length, total: sets.length })}</dd></div>
      </dl>}
      {summary?.savedPhase === 'setup' && <p>{t(key + 'setupResume')}</p>}
      {saved && <p role="status">{t(key + 'restored')}</p>}
      {prepared && <button type="button" className={button + ' bg-primary text-primary-foreground font-semibold'} disabled={busy || !saved && (!!error || !MIRROR_FITNESS_CARDS_ENABLED)} onClick={() => void confirm()}>{t(busy ? key + 'verifying' : saved ? key + 'ackRetry' : key + 'confirm')}</button>}
      <button type="button" className={button} onClick={close}>{t(key + 'cancel')}</button>
    </div>
  </ConnectionManageSurface>;
}
